import React, { type FC } from 'react'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { ModuleContext, ModuleMoreLikeThis } from '../types'
import { MoreLikeThisTabs } from './tabs'
import { MoreLikeThisCarousels } from './carousels'
import { breakpoint, styled } from '@moonpig/launchpad-utils'
import { Box } from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { useProductStore } from '../../contexts/product'
import type { ProductInfoProduct } from '../../types'

const MORE_LIKE_THIS_EXPERIMENT_KEY = 'search-use-more-like-this-v3'

export type ProductWithRecommendations = ProductInfoProduct & {
  description: string
  frequentlyBoughtTogether: { products: ProductInfoProduct[] }
  peopleAlsoViewed: { products: (ProductInfoProduct | null)[] | null }
}

const DEFAULT_SEARCH_CRITERIA = {
  searchTerm: '',
  filters: { groupedFacets: [] },
}

const StyledTabsWrapper = styled(Box)`
  ${s({
    overflow: 'hidden',
    mt: { md: 10 },
    mx: -5,
    pb: { xs: 3, md: 8 },
  })}

  ${breakpoint('md')} {
    ${s({
      mx: -10,
    })}
  }
`

type Props = {
  module: ModuleMoreLikeThis
  context: ModuleContext
}

export const MoreLikeThisModule: FC<Props> = ({ module: _module, context }) => {
  const [{ product: selectedProduct }] = useProductStore()
  const showTabs =
    useExperiment(MORE_LIKE_THIS_EXPERIMENT_KEY, { fallback: 'control' }) ===
    'tabs'

  const showCarousels =
    useExperiment(MORE_LIKE_THIS_EXPERIMENT_KEY, { fallback: 'control' }) ===
    'carousels'

  const searchCriteria =
    context.searchCriteria /* istanbul ignore next */ ?? DEFAULT_SEARCH_CRITERIA

  if (!selectedProduct) {
    return null
  }

  return (
    <StyledTabsWrapper
      data-testid="module-more-like-this"
      id="module-more-like-this"
    >
      {showTabs && (
        <MoreLikeThisTabs
          key={selectedProduct.id}
          searchCriteria={searchCriteria}
          baseProduct={selectedProduct}
        />
      )}
      {showCarousels && (
        <MoreLikeThisCarousels
          searchCriteria={searchCriteria}
          baseProduct={selectedProduct}
        />
      )}
    </StyledTabsWrapper>
  )
}
